import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Order } from '@mayksel/types';
import type { PaginatedResponse } from '@mayksel/types';
import type { CreateOrderInput, UpdateOrderInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export interface OrderFilters {
  search?: string;
  bucketId?: string;
  companyId?: string;
  status?: string;
  stageId?: string;
  priority?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function useOrders(filters: OrderFilters = {}) {
  const { getToken, workspaceId } = useApiOpts();
  const {
    search, bucketId, companyId, status, stageId, priority,
    page = 1, limit = 50, sortBy = 'created_at', sortDir = 'desc',
  } = filters;

  return useQuery<PaginatedResponse<Order>>({
    queryKey: ['orders', workspaceId, search, bucketId, companyId, status, stageId, priority, page, limit, sortBy, sortDir],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (bucketId) params.set('bucketId', bucketId);
      if (companyId) params.set('companyId', companyId);
      if (status) params.set('status', status);
      if (stageId) params.set('stageId', stageId);
      if (priority) params.set('priority', priority);
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);

      const opts: { token?: string | undefined; workspaceId?: string | undefined } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.get<PaginatedResponse<Order>>(
        `/v1/orders?${params.toString()}`,
        opts,
      );
    },
    enabled: !!workspaceId,
  });
}

export function useOrder(id: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<Order>({
    queryKey: ['orders', workspaceId, id],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Order>(`/v1/orders/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId && !!id,
  });
}

export function useCreateOrder() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateOrderInput) => {
      const token = await getToken();
      return api.post<Order>('/v1/orders', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', workspaceId] });
    },
  });
}

export function useUpdateOrder(id: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateOrderInput) => {
      const token = await getToken();
      return api.patch<Order>(`/v1/orders/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', workspaceId] });
    },
  });
}

export function useDeleteOrder() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(`/v1/orders/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', workspaceId] });
    },
  });
}
